import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../firebaseConfig'; // Import Firebase config
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const MentoringPage = () => {
  const { currentUser } = useAuth();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingTopic, setBookingTopic] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [uniqueSubjects, setUniqueSubjects] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMentors();
  }, []);

  // Fetch mentors from Firestore
  const fetchMentors = async () => {
    try {
      setLoading(true);
      const mentorsRef = collection(db, 'mentors');
      const mentorsSnapshot = await getDocs(mentorsRef);
      const mentorsData = mentorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMentors(mentorsData);
      
      // Extract unique subjects
      const allExpertise = mentorsData.flatMap(mentor => mentor.expertise);
      const unique = [...new Set(allExpertise)].sort();
      setUniqueSubjects(unique);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter mentors based on search and subject filter
  const filteredMentors = mentors.filter(mentor => {
    const matchesSearch = mentor.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         mentor.expertise.some(exp => exp.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         mentor.about.toLowerCase().includes(searchQuery.toLowerCase());
                         
    const matchesSubject = subjectFilter === 'all' || mentor.expertise.includes(subjectFilter);
    
    return matchesSearch && matchesSubject;
  });

  // Handle mentor selection
  const handleMentorSelect = (mentor) => {
    if (!currentUser) {
      // Redirect to login if not authenticated
      alert('Please login to book a session');
      return;
    }
    setSelectedMentor(mentor);
    setShowBookingModal(true);
    setCurrentStep(1);
  };

  // Initialize Razorpay
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Handle payment for paid sessions
  const handlePayment = async (bookingData) => {
    const res = await loadRazorpay();
    
    if (!res) {
      alert('Razorpay SDK failed to load. Please check your connection.');
      return;
    }

    const options = {
      key: "rzp_live_rFvA9VV5i4vRQV", // Your Razorpay API key
      amount: parseFloat(selectedMentor.hourlyRate.replace(/[^0-9.-]+/g, '')) * 100, // Convert to paise
      currency: 'INR',
      name: 'SKYSEP',
      description: `Mentoring Session with ${selectedMentor.name}`,
      handler: function (response) {
        // Payment successful, save booking to Firestore
        saveBookingToFirestore({
          ...bookingData,
          paymentId: response.razorpay_payment_id,
          paymentStatus: 'paid'
        });
      },
      prefill: {
        name: currentUser.displayName || '',
        email: currentUser.email,
        contact: currentUser.phoneNumber || ''
      },
      theme: {
        color: '#2C5282'
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  // Save booking to Firestore
  const saveBookingToFirestore = async (bookingData) => {
    try {
      setProcessing(true);
      const bookingsRef = collection(db, 'bookings');
      await addDoc(bookingsRef, {
        ...bookingData,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        createdAt: serverTimestamp(),
        status: 'confirmed'
      });

      // Also create a notification for the mentor
      const notificationsRef = collection(db, 'notifications');
      await addDoc(notificationsRef, {
        mentorId: selectedMentor.id,
        type: 'new_booking',
        message: `New booking from ${currentUser.email}`,
        read: false,
        createdAt: serverTimestamp()
      });

      alert('Your mentoring session has been booked successfully!');
      resetBookingForm();
    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Failed to save booking. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle booking submission
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const bookingData = {
        mentorId: selectedMentor.id,
        mentorName: selectedMentor.name,
        date: bookingDate,
        time: bookingTime,
        topic: bookingTopic,
        fee: selectedMentor.hourlyRate,
        sessionType: 'one-on-one'
      };

      if (selectedMentor.sessionType === 'free') {
        // For free sessions, directly save to Firestore
        await saveBookingToFirestore({
          ...bookingData,
          paymentStatus: 'free'
        });
      } else {
        // For paid sessions, initiate Razorpay payment
        await handlePayment(bookingData);
      }
    }
  };

  // Reset booking form
  const resetBookingForm = () => {
    setBookingDate('');
    setBookingTime('');
    setBookingTopic('');
    setShowBookingModal(false);
    setCurrentStep(1);
    setSelectedMentor(null);
  };

  // Get available dates (next 14 days excluding unavailable days)
  const getAvailableDates = () => {
    if (!selectedMentor) return [];
    
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
      
      if (selectedMentor.availableDays.includes(dayOfWeek)) {
        dates.push({
          date: date.toISOString().split('T')[0],
          dayOfWeek
        });
      }
    }
    
    return dates;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2C5282]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              One-on-One Mentoring
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl mb-8 text-blue-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Get personalized guidance from India's top educators. Our mentors provide tailored advice, answer your specific questions, and help you develop a winning strategy for your academic journey.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <a
                href="#mentors"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-md"
              >
                Book a Session
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
              >
                How It Works
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              How Mentoring Works
            </motion.h2>
            <motion.p 
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              A simple three-step process to get the personalized guidance you need
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-[#2C5282] mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#2C5282] mb-2">1. Choose a Mentor</h3>
              <p className="text-gray-600">Browse our experienced mentors and select one who specializes in your areas of interest.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-[#2C5282] mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#2C5282] mb-2">2. Schedule a Session</h3>
              <p className="text-gray-600">Book a convenient time slot from your mentor's available schedule.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-[#2C5282] mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#2C5282] mb-2">3. Meet & Learn</h3>
              <p className="text-gray-600">Connect with your mentor via video call for a personalized one-on-one session.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mentors Section */}
      <section id="mentors" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2 
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Meet Our Mentors
            </motion.h2>
            <motion.p 
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Learn from India's top educators with years of experience
            </motion.p>
          </div>

          {/* Search and Filter */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-2/3">
                <div className="relative">
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-[#2C5282] focus:border-[#2C5282]"
                    placeholder="Search by name, subject, or expertise..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3">
                <select
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-[#2C5282] focus:border-[#2C5282]"
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                >
                  <option value="all">All Subjects</option>
                  {uniqueSubjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Mentors Grid */}
          {filteredMentors.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No mentors found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSubjectFilter('all');
                }}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2C5282] hover:bg-[#1A365D]"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredMentors.map((mentor) => (
                <motion.div
                  key={mentor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <img
                        src={mentor.image || "/images/default-avatar.jpg"}
                        alt={mentor.name}
                        className="h-16 w-16 rounded-full object-cover mr-4 border-2 border-[#2C5282]"
                      />
                      <div>
                        <h3 className="text-lg font-bold text-[#2C5282]">{mentor.name}</h3>
                        <p className="text-gray-600">{mentor.role}</p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <svg className="w-4 h-4 mr-1 text-[#2C5282]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {mentor.qualification}
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <svg className="w-4 h-4 mr-1 text-[#2C5282]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {mentor.experience}
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[#2C5282] mb-2">Expertise:</h4>
                      <div className="flex flex-wrap gap-2">
                        {mentor.expertise.map((subject, index) => (
                          <span
                            key={index}
                            className="inline-block bg-blue-50 text-[#2C5282] text-xs font-medium px-2.5 py-0.5 rounded"
                          >
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm text-gray-700 ml-1">{mentor.rating}</span>
                        <span className="text-sm text-gray-500 ml-1">({mentor.reviewCount} reviews)</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {mentor.sessionCount} sessions
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{mentor.about}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-bold text-[#2C5282]">{mentor.hourlyRate}<span className="text-sm text-gray-600">/hour</span></div>
                      <button
                        onClick={() => handleMentorSelect(mentor)}
                        className="px-4 py-2 bg-[#2C5282] hover:bg-[#1A365D] text-white text-sm font-medium rounded transition-colors duration-300"
                      >
                        Book Session
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Take Your Learning to the Next Level?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Book a mentoring session today and get personalized guidance from India's top educators.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="#mentors"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-md"
            >
              Book a Session Now
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showBookingModal && selectedMentor && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-[#2C5282] mb-4">
                      Book a Session with {selectedMentor.name}
                    </h3>
                    
                    {/* Step 1: Select Date and Time */}
                    {currentStep === 1 && (
                      <div>
                        <div className="mb-4">
                          <label htmlFor="booking-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Date
                          </label>
                          <select
                            id="booking-date"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#2C5282] focus:border-[#2C5282] sm:text-sm rounded-md"
                            value={bookingDate}
                            onChange={(e) => setBookingDate(e.target.value)}
                            required
                          >
                            <option value="">Select a date</option>
                            {getAvailableDates().map((dateOption, index) => (
                              <option key={index} value={dateOption.date}>
                                {new Date(dateOption.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="booking-time" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Time
                          </label>
                          <select
                            id="booking-time"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#2C5282] focus:border-[#2C5282] sm:text-sm rounded-md"
                            value={bookingTime}
                            onChange={(e) => setBookingTime(e.target.value)}
                            required
                          >
                            <option value="">Select a time</option>
                            {selectedMentor.availableTimeSlots.map((timeSlot, index) => (
                              <option key={index} value={timeSlot}>
                                {timeSlot}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {/* Step 2: Session Details */}
                    {currentStep === 2 && (
                      <div>
                        <div className="mb-4">
                          <label htmlFor="booking-topic" className="block text-sm font-medium text-gray-700 mb-1">
                            Session Topic/Focus
                          </label>
                          <textarea
                            id="booking-topic"
                            rows={3}
                            className="shadow-sm focus:ring-[#2C5282] focus:border-[#2C5282] mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                            placeholder="Describe what you'd like to focus on in this session..."
                            value={bookingTopic}
                            onChange={(e) => setBookingTopic(e.target.value)}
                            required
                          ></textarea>
                        </div>
                        
                        <div className="rounded-md bg-blue-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-[#2C5282]" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-[#2C5282]">Booking Information</h3>
                              <div className="mt-2 text-sm text-[#1A365D]">
                                <p>Session with {selectedMentor.name}</p>
                                <p>Date: {bookingDate && new Date(bookingDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                                <p>Time: {bookingTime}</p>
                                <p className="mt-2 font-medium">Fee: {selectedMentor.hourlyRate}</p>
                                {selectedMentor.sessionType === 'free' && (
                                  <p className="mt-1 text-green-600 font-medium">This is a FREE session</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2C5282] text-base font-medium text-white hover:bg-[#1A365D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2C5282] sm:ml-3 sm:w-auto sm:text-sm ${
                    (currentStep === 1 && (!bookingDate || !bookingTime)) || 
                    (currentStep === 2 && !bookingTopic) || 
                    processing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={handleBookingSubmit}
                  disabled={(currentStep === 1 && (!bookingDate || !bookingTime)) || (currentStep === 2 && !bookingTopic) || processing}
                >
                  {processing ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    currentStep === 1 ? 'Continue' : 'Confirm Booking'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2C5282] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    if (currentStep === 2) {
                      setCurrentStep(1);
                    } else {
                      setShowBookingModal(false);
                    }
                  }}
                  disabled={processing}
                >
                  {currentStep === 1 ? 'Cancel' : 'Back'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Modal */}
      {showSuccessModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Booking Confirmed!
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Your mentoring session has been successfully booked. You will receive a confirmation email with the session details shortly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2C5282] text-base font-medium text-white hover:bg-[#1A365D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2C5282] sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentoringPage;